import React, { useEffect, useState, memo, useRef } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../../shared/hooks/useApi';
import CustomTable from '../../../../../shared/components/table/custom-table';
import { LoaderComponent } from '../../../../../shared/components/loaders/page-loader';
import { fetchProductaddDetails } from './product-api';
import { useTransactionCertificateStore } from '../../../../../store/transaction-certificates.store';

interface RenderDetailPanelProps {
  row: any;
}

// Column Definitions
const processorColumns = [
  { accessorKey: 'tcProductLastProcessorName', header: 'Last Processor Name', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorTeId', header: 'Last Processor TE-ID', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorLicenseNo', header: 'Last Processor License No.', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductLastProcessorCountryDesc', header: 'Last Processor Country', minSize: 60, maxSize: 150 },
];

const materialColumns = [
  { accessorKey: 'tcProductInfoFiberLength', header: 'Fiber Length (mm)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoYarnCount', header: 'Yarn Count', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightAndConstruct', header: 'Fabric Weight and Construct (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoFabricWeightOfFinishedProduct', header: 'Fabric Weight of Finished Product (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductInfoTrimsExcluded', header: 'Trim Excluded', minSize: 60, maxSize: 150 },
];

const standardColumns = [
  { accessorKey: 'tcProductStandardLabelGrade', header: 'Standard Label Grade', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeStandardLabel', header: 'Raw Material', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductCertifiedWeight', header: 'Certified Weight (kg)', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductStandardLabelGradeDescription', header: 'Standard Label Grade Description', minSize: 60, maxSize: 150 }
];

const rawMaterialColumns = [
  { accessorKey: 'tcProductRawMaterialCode', header: 'Raw Material Code', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialPercentage', header: 'Raw Material Percentage', minSize: 60, maxSize: 150 },
  { accessorKey: 'tcProductRawMaterialCodeDescription', header: 'Raw Material Description', minSize: 60, maxSize: 150 },
];

const RenderDetailPanel = memo(({ row }: RenderDetailPanelProps) => {
  const rowData = row.original;
  const { TE_ID } = useParams();

  const { loading, executeGet } = useApi();
  const [productData, setProductData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const productId = rowData?.productId;
  const shipmentId = rowData?.shipmentId;
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!productId || !TE_ID || !shipmentId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
console.log(shipmentId,"32")
    fetchProductaddDetails(executeGet, TE_ID, productId, shipmentId)
      .then((data) => {
        data.data.tcProductInformation = data.data.tcProductInformation.map((dat: any) => {
          return {
            ... dat,
            'tcProductLastProcessorCountryDesc': dat.tcProductLastProcessorCountry ? 
                      useTransactionCertificateStore.getState()?.getCountryCodes?.find((option: any) => option.value === dat.tcProductLastProcessorCountry)?.label || "" : "",
          }
        })
        setProductData(data.data.tcProductInformation)
      })
      .catch((err) => {console.log(err,"32");setError(err.message)});
  }, [productId, shipmentId, TE_ID, executeGet]);

  if (loading) {
    return <LoaderComponent />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!productData || productData.length === 0) {
    return <Typography>No product details available.</Typography>;
  }

  // Data Extraction: Since productData is an array
  const product = productData[0]; // Assuming one item per detail panel (adjust if multiple)

  const processorInfo = {
    tcProductLastProcessorName: product.tcProductLastProcessorName,
    tcProductLastProcessorTeId: product.tcProductLastProcessorTeId,
    tcProductLastProcessorLicenseNo: product.tcProductLastProcessorLicenseNo,
    tcProductLastProcessorCountry: product.tcProductLastProcessorCountry,
    tcProductLastProcessorCountryDesc: product.tcProductLastProcessorCountryDesc
  };

  const materialInfo = {
    tcProductInfoFiberLength: product.tcProductInfoFiberLength,
    tcProductInfoYarnCount: product.tcProductInfoYarnCount,
    tcProductInfoFabricWeightAndConstruct: product.tcProductInfoFabricWeightAndConstruct,
    tcProductInfoFabricWeightOfFinishedProduct: product.tcProductInfoFabricWeightOfFinishedProduct,
    tcProductInfoTrimsExcluded: product.tcProductInfoTrimsExcluded,
  };

  const standardInfo = product.tcProductStandardInformation || [];
  const rawMaterialInfo = product.tcProductRawMaterialInformation || [];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 5,
        gap: 4,
        width: '100%',
      }}
    >
      {/* Processor Info */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Processor Information
        </Typography>
        <CustomTable
          columns={processorColumns}
          data={[processorInfo]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Fabric & Material Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Fabric and Material Details
        </Typography>
        <CustomTable
          columns={materialColumns}
          data={[materialInfo]}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Standard Details */}
      <Box sx={{ width: '100%', textAlign: 'left', mb: 3 }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Standard Details
        </Typography>
        <CustomTable
          columns={standardColumns}
          data={standardInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
      <Divider />

      {/* Raw Material Composition */}
      <Box sx={{ width: '100%', textAlign: 'left' }} display="grid">
        <Typography variant="body2" fontWeight={'400'} sx={{ mb: 1 }}>
          Raw Material Composition
        </Typography>
        <CustomTable
          columns={rawMaterialColumns}
          data={rawMaterialInfo}
          enableTopToolbar={false}
          enableRowActions={false}
          enableBottomToolbar={false}
        />
      </Box>
    </Box>
  );
});

export default RenderDetailPanel;
